/**
 * Notifications permission — the step after Profile info.
 *
 * Shaped after docs/references/09-permissions.png: a dialog, with "Not now"
 * and "Continue" as text buttons.
 *
 * ONLY notifications are asked for here. The camera is asked for the first
 * time someone opens the camera sheet, and the microphone the first time
 * they record a voice note — both already do that on their own, via
 * getUserMedia in CameraSheet.jsx and ChatRoom.jsx. Asking at registration
 * for things nobody has reached yet is the pattern people decline
 * (owner, 2026-11-05).
 *
 * The browser only shows its prompt in response to a click, so Continue is
 * what triggers it; asking on mount would be dismissed unread.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './Permissions.css'

/** Ask the browser for notifications. Never throws; returns what happened. */
async function askNotifications () {
  if (typeof Notification === 'undefined') return 'unsupported'
  if (Notification.permission !== 'default') return Notification.permission
  try {
    return await Notification.requestPermission()
  } catch {
    return 'denied'
  }
}

export default function Permissions () {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  const done = () => navigate('/chats', { replace: true })

  async function allow () {
    setBusy(true)
    await askNotifications()
    setBusy(false)
    done()            // granted or not, the app works
  }

  return (
    <main className="perm">
      <div className="perm__card" role="dialog" aria-labelledby="perm-title">
        <div className="perm__banner" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="38" height="38" fill="none">
            <path d="M12 3a6 6 0 0 0-6 6v3.6L4.5 16h15L18 12.6V9a6 6 0 0 0-6-6Z"
              stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M10 19a2 2 0 0 0 4 0"
              stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </div>

        <div className="perm__body">
          <h1 className="perm__title" id="perm-title">Notifications</h1>
          <p className="perm__text">
            To let you know when a message arrives while ZIvapp is closed,
            allow ZIvapp to send you notifications.
          </p>
        </div>

        <div className="perm__actions">
          <button type="button" className="perm__action" onClick={done} disabled={busy}>
            NOT NOW
          </button>
          <button type="button" className="perm__action perm__action--go" onClick={allow} disabled={busy}>
            {busy ? 'ASKING…' : 'CONTINUE'}
          </button>
        </div>
      </div>
    </main>
  )
}
